import { useState, useMemo } from "react";
import styles from "./InventoryItemActions.module.scss";
import {
  AppDropdownMenu,
  IDropdownMenuItem,
} from "../../../../common/components/AppDropdownMenu";
import { HistoryDialog } from "../../../../common/components/history/HistoryDialog";
import { NotesDialog } from "../../../../common/components/common/NotesDialog/NotesDialog";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { INVENTORY_HISTORY_CONFIG } from "../../../../common/utilities/inventoryAuditLogUtility";
import { IconType } from "../../../../common/icons/Icon";
import { IInventoryItemInputModel } from "../models/inventoryNomenclatureModels";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useAppContext } from "../../../../AppContext";

interface IInventoryItemActionsProps {
  item: IInventoryItemInputModel;
  endpoints: {
    getAll: string;
    getAllWithDeleted: string;
    getById: string;
    getAllCategories: string;
    getAllCategorized: string;
    getItemNotes: string;
    addItemNote: string;
    updateItemNote: string;
    deleteItemNote: string;
    toggleDeletionStatus: string;
  };
  studioSettings?: IStudioSettingsModel;
  canAdjust: boolean;
  onAdjust: (item: IInventoryItemInputModel) => void;
  onEdit?: (item: IInventoryItemInputModel) => void;
}

export const InventoryItemActions = ({
  item,
  endpoints,
  studioSettings,
  canAdjust,
  onAdjust,
  onEdit,
}: IInventoryItemActionsProps) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const { mutate } = useApiMutation();
  const { selectedStudioId } = useAppContext();

  const invalidateKeys = useMemo(
    () => [
      [endpoints.getAllCategorized, selectedStudioId],
      [endpoints.getAll, selectedStudioId],
      [endpoints.getAllWithDeleted, selectedStudioId],
      [endpoints.getById, selectedStudioId],
      [endpoints.getAllCategories, selectedStudioId],
    ],
    [endpoints, selectedStudioId],
  );

  const handleDelete = () => {
    showCustomConfirmDialog(
      "Сигурни ли сте, че искате да изтриете този запис?",
      () => {
        mutate({
          endpoint: `${endpoints.toggleDeletionStatus}?itemId=${item.id}`,
          method: "put",
          invalidateKeys,
        });
      },
      () => {},
      "warning",
    );
  };

  const handleRestore = () => {
    showCustomConfirmDialog(
      "Да възстановим ли записа?",
      () => {
        mutate({
          endpoint: `${endpoints.toggleDeletionStatus}?itemId=${item.id}`,
          method: "put",
          invalidateKeys,
        });
      },
      () => {},
      "success",
    );
  };

  const menuItems = useMemo<IDropdownMenuItem[]>(() => {
    const items: IDropdownMenuItem[] = [];

    if (!item.isDeleted && onEdit) {
      items.push({
        label: "Редактиране",
        icon: IconType.EditIcon,
        onClick: () => onEdit(item),
      });
    }

    items.push(
      {
        label: "История",
        icon: IconType.ClockIcon,
        onClick: () => setShowHistory(true),
      },
      {
        label: "Бележки",
        icon: IconType.AddNote,
        onClick: () => setShowNotes(true),
        className: styles.notes_item,
      },
    );

    if (canAdjust && !item.isDeleted) {
      items.push({
        label: "Корекция на количество",
        icon: IconType.Filters,
        onClick: () => onAdjust(item),
      });
    }

    if (item.isDeleted) {
      items.push(
        { label: "", separator: true },
        {
          label: "Възстановяване",
          icon: IconType.PutBackIcon,
          onClick: handleRestore,
        },
      );
    } else {
      items.push(
        { label: "", separator: true },
        {
          label: "Изтриване",
          icon: IconType.DeleteIcon,
          onClick: handleDelete,
        },
      );
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAdjust, item, onAdjust, onEdit]);

  return (
    <>
      <AppDropdownMenu
        items={menuItems}
        triggerIconSize="18px"
        triggerIconColor="rgba(255, 255, 255, 0.9)"
        menuClassName={styles.menu}
      />

      <HistoryDialog
        entityId={item.id!}
        entityCreatedOn={new Date().toISOString()}
        timezone={studioSettings?.timezone}
        showDialog={showHistory}
        onHide={() => setShowHistory(false)}
        label="История на продукта"
        config={INVENTORY_HISTORY_CONFIG}
      />

      <NotesDialog
        entityId={item.id}
        showDialog={showNotes}
        onHide={() => setShowNotes(false)}
        label="Бележки към продукта"
        getEndpoint={endpoints.getItemNotes}
        createEndpoint={endpoints.addItemNote}
        updateEndpoint={endpoints.updateItemNote}
        deleteEndpoint={endpoints.deleteItemNote}
        noResultsLabel="Към момента няма бележки за този продукт."
        additionalInvalidateKeys={[
          [endpoints.getAllCategorized, selectedStudioId],
          [endpoints.getAll, selectedStudioId],
          [endpoints.getAllWithDeleted, selectedStudioId],
        ]}
      />
    </>
  );
};
